//
//  trialConfiguration.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the trial configuration and trial item pages.
//

import { Permission } from "src/api/Tmd.dtos";

import {
    bulkTourTargets,
    helpTourTargets,
    importTourTargets,
    preferenceTourTargets,
    selectionTourTargets
} from "../helpTourTargets";
import { builderStops, emailRecipientsGuide, entryGuide, versionStops } from "./commonGuides";
import {
    actionsStop,
    cards,
    column,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    saveStop,
    stop,
    type PageGuide,
    type PageGuideColumn,
    type PageGuideStop
} from "./pageGuide";

/** The fields shared by the trial's item template pages, up to the selectors. */
const templateFields = (
    versionPath: "allowVersions" | "allowIntraVersions",
    withSortOrder: boolean
): PageGuideStop[] => [
    field(
        "name",
        "Name",
        "The item's name, shown in item lists, on the dashboard and in emails about the item."
    ),
    field(
        "categoryId",
        "Category",
        "The item category the template belongs to, which groups items in lists and overviews."
    ),
    field(
        "description",
        "Description",
        "What the item collects and when it is needed, shown beside the item's name in lists."
    ),
    field(
        "rank",
        "Rank",
        "The order the template is listed in within its category; lower numbers are listed first."
    ),
    field(
        "active",
        "Active",
        "Clear to retire the template so no new items are created from it. Items already created are kept."
    ),
    field(
        versionPath,
        versionPath === "allowVersions" ? "Allow Versions" : "Allow Intra Versions",
        "Whether items from this template can be given new versions, for example a re-signed agreement, keeping the earlier versions readable."
    ),
    field(
        "requiresReview",
        "Requires Review",
        "When enabled, submitted items wait for review before being marked Complete. Otherwise, they become Complete on submission."
    ),
    field("initialVersion", "Initial Version", "The version name new items start at, such as 1.0."),
    ...(withSortOrder
        ? [
              field(
                  "sortOrderId",
                  "Sort Versions",
                  "How new items order their version history: Default, or by version date or name, ascending or descending."
              )
          ]
        : [])
];

const selectorNote =
    "Selectors choose who gets the item. Leave them all empty to apply the template to everyone; when several are set, an item is created only where every kind of selector matches.";

const institutionTemplate: PageGuide = {
    intro: "A template for an item each trial institution completes, such as an agreement or governance approval. Items are created for every site the template applies to, and its layout sets what they collect.",
    stops: [
        ...templateFields("allowVersions", true),
        field(
            "trialInstitutionItemTemplateGroups",
            "Groups",
            "Apply the item only to sites taking part through these groups."
        ),
        field(
            "trialInstitutionItemTemplateInstitutions",
            "Institutions",
            "Apply the item only to these institutions, for items needed at particular sites."
        ),
        field(
            "trialInstitutionItemTemplateCapabilities",
            "Capabilities",
            "Apply the item only to sites with these capabilities, for example a treatment technique that needs its own credentialing."
        ),
        field(
            "trialInstitutionItemTemplateTags",
            "Tags",
            "Apply the item only to sites with these institution tags."
        ),
        ...builderStops(Permission.TrialInstitutionItemUpdate),
        saveStop(
            "Submit saves the template and updates the sites' items: sites that now match get the item, items at sites that no longer match are made inactive, and matching inactive items are reactivated.",
            Permission.TrialInstitutionItemUpdate
        )
    ],
    notes: [selectorNote]
};

const participantTemplate: PageGuide = {
    intro: "A template for an item collected for participants, such as a consent or eligibility record. Items are created for every participant the template applies to, and its layout sets what they collect.",
    stops: [
        ...templateFields("allowVersions", true),
        field(
            "patientItemTemplateGroups",
            "Groups",
            "Apply the item only to participants at sites taking part through these groups."
        ),
        field(
            "patientItemTemplateInstitutions",
            "Institutions",
            "Apply the item only to participants registered at these institutions."
        ),
        field(
            "patientItemTemplatePatients",
            "Participants",
            "Apply the item only to these participants, for items needed for particular people."
        ),
        field(
            "patientItemTemplateInstitutionCapabilities",
            "Institution Capabilities",
            "Apply the item only to participants at sites with these capabilities."
        ),
        field(
            "patientItemTemplateInstitutionTags",
            "Institution Tags",
            "Apply the item only to participants at sites with these institution tags."
        ),
        field(
            "patientItemTemplateTags",
            "Participant Tags",
            "Apply the item only to participants with these participant tags."
        ),
        ...builderStops(Permission.TrialPatientItemTemplatesUpdate),
        saveStop(
            "Submit saves the template and updates the participants' items: participants who now match get the item, and items for participants who no longer match are made inactive.",
            Permission.TrialPatientItemTemplatesUpdate
        )
    ],
    notes: [selectorNote]
};

const groupTemplate: PageGuide = {
    intro: "A template for an item collected from the cooperative groups taking part in the trial. Its layout sets what the items collect.",
    stops: [
        ...templateFields("allowIntraVersions", false),
        field(
            "trialGroupItemTemplateGroups",
            "Groups",
            "The groups the item is collected from. Leave empty to collect it from every group on the trial."
        ),
        ...builderStops(Permission.TrialGroupItemTemplatesUpdate),
        saveStop(
            "Submit saves the template and its layout.",
            Permission.TrialGroupItemTemplatesUpdate
        )
    ]
};

const trialItem: PageGuide = {
    intro: "One trial item, such as a protocol document or an approval: its template, current version, status and who is responsible. Enter Data opens the collected data.",
    stops: [
        field(
            "trialItemTemplateId",
            "Trial Item Template",
            "The template the item was created from, which sets the questions it collects. It cannot be changed once the item exists."
        ),
        field(
            "description",
            "Description",
            "Extra detail that tells this item apart from others from the same template, such as the document's purpose."
        ),
        ...versionStops(),
        field(
            "currentVersion.trialItemStatusId",
            "Trial Item Status",
            "Where the current version is, from the trial item statuses maintained in settings. It is shown in the trial items list and on the dashboard."
        ),
        field(
            "assignmentTypeId",
            "Assign To",
            "Whether a person or a role is responsible for the item. The matching field appears below."
        ),
        field(
            "assignedToId",
            "Assigned To",
            "The person responsible, when the item is assigned to a person. It appears on their dashboard."
        ),
        field(
            "assignedRoleId",
            "Assigned Role",
            "The role responsible, when the item is assigned to a role; it appears on the dashboard of everyone holding the role."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the item from the trial's items, for example when it no longer applies."
        ),
        saveStop(
            "Submit saves the item's details. The collected data is saved on the Enter Data page.",
            Permission.TrialItemUpdate
        )
    ],
    rules: ["Trial Item Template is required."]
};

const bulkGuide = (owner: string, templateKind: string, permission: Permission): PageGuide => ({
    intro: `Update the same item for many ${owner} at once, instead of opening each one in turn.`,
    stops: [
        stop(
            bulkTourTargets.template,
            "Choose the item",
            `Pick the ${templateKind} item template to update. Every ${owner.replace(/s$/, "")} with a current item from it is loaded below, one panel each.`
        ),
        stop(
            bulkTourTargets.entry,
            "One panel per entry",
            "Each panel holds one entry, headed by its owner and version. Expand and edit as many as you need; a panel you cannot edit opens read-only.",
            permission
        ),
        stop(
            bulkTourTargets.actions,
            "Saving together",
            "Submit saves every panel in one go. Panels that cannot be saved are reported individually without undoing the others; Save Draft keeps incomplete entries.",
            permission
        )
    ],
    notes: [
        "If the entries cannot be loaded, Retry loading tries again without losing the chosen template."
    ]
});

const importGuide = (
    intro: string,
    permission: Permission,
    columns: PageGuideColumn[]
): PageGuide => ({
    intro,
    stops: [
        stop(
            importTourTargets.source,
            "Choose a source",
            "Select where to copy from, such as the template library or another trial. Changing the source clears the ticked templates.",
            permission
        ),
        stop(
            selectionTourTargets.category,
            "Categories",
            "Templates are grouped by category; each heading counts the templates in it. Select a heading to open or close it."
        ),
        grid(
            "Tick the templates to copy. Templates this trial already has cannot be ticked.",
            columns
        ),
        stop(
            importTourTargets.actions,
            "Review, then import",
            "Review Import shows exactly what will be created. Nothing is copied until you confirm in the review.",
            permission
        )
    ]
});

const importColumns = {
    name: column("Name", "The template's name, as it will appear in this trial."),
    description: column("Description", "What the template's items collect."),
    initialVersion: column("Initial Version", "The version name new items will start at."),
    scope: column("Scope", "Where the template comes from: the library or another trial."),
    selectors: column(
        "Selectors",
        "The groups, institutions, capabilities or tags that decide who gets the item; review them after importing."
    )
};

/**
 * A capability or tag selection page.
 *
 * Postconditions: The stops walk the add controls, a category of selections and the save bar.
 */
const selectionGuide = (
    intro: string,
    thing: string,
    addLabel: string,
    permission: Permission,
    effect: string,
    extraStops: PageGuideStop[] = []
): PageGuide => ({
    intro,
    stops: [
        stop(
            selectionTourTargets.actions,
            `Adding ${thing}`,
            `${addLabel} opens the site-wide catalogue to choose ${thing} for this trial. Show Inactive includes ${thing} that have been removed, so they can be restored.`,
            permission
        ),
        ...extraStops,
        stop(
            selectionTourTargets.save,
            "Saving",
            "Changes are held on the page until you Submit. Submit And Return also goes back to the configuration page; Cancel leaves without saving.",
            permission
        )
    ],
    notes: [
        effect,
        `Removing ${thing} from the trial suspends them for the trial without clearing what sites already have recorded; restoring them makes those records count again.`
    ]
});

const trialConfigurationGuides: Record<string, PageGuide> = {
    "/trials/:trialId/configuration": {
        intro: "Configure how the trial works. Each card opens one area; you only see the areas you can manage.",
        stops: [
            cards(
                item(
                    "Institution Item Templates",
                    "What each site completes for the trial, such as agreements and approvals, and which sites get each item."
                ),
                item(
                    "Trial Group Item Templates",
                    "What is collected from the cooperative groups taking part in the trial."
                ),
                item(
                    "Participant Item Templates",
                    "What is collected for participants, such as consent and eligibility records, and which participants get each item."
                ),
                item(
                    "Institution Capabilities",
                    "The institution capabilities the trial permits or requires of its sites."
                ),
                item(
                    "Institution Tags",
                    "The institution tags the trial uses to describe its sites."
                ),
                item(
                    "Participant Tags",
                    "The participant tags the trial uses to classify participants."
                ),
                item(
                    "Emails",
                    "Who receives each email the trial sends, and what sites and participants may change."
                ),
                item(
                    "Personnel Email Preferences",
                    "Where individual people on the trial have chosen their email to go, and resetting those choices."
                ),
                item(
                    "QA Program Checklist",
                    "The categories and tasks of the trial's quality-assurance program."
                ),
                item("API", "How the trial connects to OpenClinica and its registration website.")
            )
        ]
    },
    "/trials/:trialId/configuration/qa-program-checklist": {
        intro: "The trial's own copy of the QA program checklist: its categories and tasks. Changes here affect this trial only.",
        stops: [
            stop(
                helpTourTargets.pageActions,
                "Checklist actions",
                "Initialize creates the trial's checklist. Import Defaults opens the global defaults; tick categories and tasks and choose Import selected to copy them. Add Category adds a category of the trial's own. View Checklist opens the checklist where progress is recorded.",
                Permission.TrialQaProgramChecklistUpdate
            )
        ],
        notes: [
            "Expand a category to add, reorder or remove its tasks.",
            "Removed categories and tasks are kept and can be shown and restored; completion history is never lost."
        ]
    },
    "/trials/:trialId/configuration/capabilities": selectionGuide(
        "The institution capabilities this trial uses, such as treatment techniques, and whether each is required of every site or only optional. Each site is checked against the required capabilities.",
        "capabilities",
        "Add Capability",
        Permission.TrialUpdate,
        "Institution and participant item templates can select sites by capability, so capabilities can decide which items a site gets.",
        [
            stop(
                selectionTourTargets.category,
                "Selected capabilities",
                "The trial's capabilities, grouped by category with a count of required ones. For each, choose Required or Optional, or remove it from the trial.",
                Permission.TrialUpdate
            )
        ]
    ),
    "/trials/:trialId/configuration/tags": selectionGuide(
        "The institution tags this trial uses to describe its sites, chosen from the site-wide catalogue.",
        "tags",
        "Add Tag",
        Permission.TrialUpdate,
        "Institution and participant item templates can select sites by tag, so tags can decide which items a site gets."
    ),
    "/trials/:trialId/configuration/participant-tags": selectionGuide(
        "The participant tags this trial uses to classify participants, chosen from the site-wide catalogue.",
        "tags",
        "Add Tag",
        Permission.TrialPatientTagSelectionsUpdate,
        "Participant item templates can select participants by tag, so tags can decide which items a participant gets."
    ),
    "/trials/:trialId/configuration/emails": emailRecipientsGuide(
        "trial",
        "Who receives each email this trial sends. The trial follows the global defaults until it sets its own; sites, participants and case reports can override the trial in turn.",
        Permission.TrialEmailRecipientsUpdate,
        [
            stop(
                helpTourTargets.pageActions,
                "External emails",
                "External Emails decides which parts of each email sites, participants and case reports may change for themselves.",
                Permission.TrialExternalEmailPolicyUpdate
            )
        ]
    ),
    "/trials/:trialId/configuration/emails/preferences": {
        intro: "People on this trial can choose where its email goes, such as another address. Set or reset those preferences for them here. Preferences change where email goes, not whether someone is a recipient.",
        stops: [
            stop(
                preferenceTourTargets.person,
                "Set someone's preferences",
                "Choose a person without preferences to start setting theirs. People who already have preferences are listed with a Reset button.",
                Permission.TrialPersonnelEmailPreferencesUpdate
            )
        ],
        notes: [
            "Reset removes a person's preferences after you confirm, so their email goes to their usual address again."
        ]
    },
    "/trials/:trialId/configuration/api": {
        intro: "How the trial connects to OpenClinica and its registration website. These settings must match the OpenClinica study build, or participant and user synchronisation fails.",
        stops: [
            field(
                "registrationWebsite",
                "Registration Website",
                "The address of the trial's online registration system, used in links for site staff."
            ),
            field(
                "openClinciaProtocolID",
                "OpenClinica Study",
                "The OpenClinica study the trial is linked to, used to import participants and set up user roles."
            ),
            field(
                "openClinciaInstitutionPrefix",
                "OpenClinica Institution Prefix",
                "The prefix OpenClinica uses for this trial's site identifiers, so sites can be matched."
            ),
            field(
                "patientDetailsCRF",
                "OpenClinica Participant Details CRF",
                "The OpenClinica form that holds participants' details, read when importing participants."
            ),
            field(
                "initialsCrfItemName",
                "OpenClinica Participant Initials Data Item",
                "The item on that form holding the participant's initials."
            ),
            field(
                "treatmentCrfItemName",
                "OpenClinica Participant Treatment Data Item",
                "The item on that form holding the participant's treatment arm, matched to the trial's treatments."
            ),
            field(
                "apiKey",
                "Api Key",
                "The key external systems use to reach the trial's API. Treat it like a password."
            ),
            saveStop(
                "Submit saves the connection settings; they are used from the next synchronisation.",
                Permission.TrialUpdate
            )
        ],
        notes: [
            "If the OpenClinica lookups cannot be loaded, Retry tries again without losing your choices."
        ]
    },
    "/trials/:trialId/configuration/institution-item-templates": {
        intro: "The templates for the items trial institutions complete, such as agreements and approvals, and which sites get each one.",
        stops: [
            filterStop(
                "Type part of a name or description to filter. Retired templates are hidden unless you switch on inactive records."
            ),
            actionsStop(
                "Import copies templates from the template library or another trial; Add builds one from scratch.",
                Permission.TrialInstitutionItemCreate
            ),
            grid("Each row is one template; select it to change it and its layout.", [
                column("Name", "The template's item name."),
                column("Description", "What the template's items collect."),
                column(
                    "Groups or Institutions",
                    "The groups or institutions the template is limited to; empty means every site."
                ),
                column("Initial Version", "The version name new items start at.")
            ])
        ]
    },
    "/trials/:trialId/configuration/institution-item-templates/import": importGuide(
        "Copy institution item templates into this trial from the template library or another trial.",
        Permission.TrialInstitutionItemTemplateImport,
        [
            importColumns.name,
            importColumns.description,
            importColumns.scope,
            importColumns.selectors,
            importColumns.initialVersion
        ]
    ),
    "/trials/:trialId/configuration/institution-item-templates/:trialInstitutionItemTemplateId":
        institutionTemplate,
    "/trials/:trialId/configuration/institution-item-templates/create": createGuide(
        institutionTemplate,
        "Add an institution item template to this trial. Importing a similar template from the library is often quicker.",
        [
            "Once saved, the item is created for every site the template applies to. Build its layout, then save again."
        ],
        { omit: ["active"] }
    ),
    "/trials/:trialId/configuration/group-item-templates": {
        intro: "The templates for the items collected from the cooperative groups taking part in the trial.",
        stops: [
            filterStop("Type part of a name or description to filter the list."),
            actionsStop(
                "Add builds a template; Import copies templates from the global library, a group or another trial.",
                Permission.TrialGroupItemTemplatesCreate
            ),
            grid("Each row is one template; select it to change it and its layout.", [
                column("Name", "The template's item name."),
                column("Description", "What the template's items collect."),
                column("Initial Version", "The version name new items start at."),
                column("Groups", "The groups the item is collected from; empty means every group.")
            ])
        ]
    },
    "/trials/:trialId/configuration/group-item-templates/import": importGuide(
        "Copy trial group item templates from the global library, a group or another trial.",
        Permission.TrialGroupItemTemplatesCreate,
        [
            importColumns.name,
            importColumns.description,
            column("Status", "Whether the template is already in this trial or can be imported."),
            importColumns.initialVersion
        ]
    ),
    "/trials/:trialId/configuration/group-item-templates/:trialGroupItemTemplateId": groupTemplate,
    "/trials/:trialId/configuration/group-item-templates/create": createGuide(
        groupTemplate,
        "Add a trial group item template. Importing a similar template is often quicker.",
        ["Once saved, build its layout, then save again."],
        { omit: ["active"] }
    ),
    "/trials/:trialId/configuration/participant-item-templates": {
        intro: "The templates for the items collected for participants, and which participants get each one.",
        stops: [
            filterStop("Type part of a name or description to filter the list."),
            actionsStop(
                "Import copies templates from the template library or another trial; Add builds one from scratch.",
                Permission.TrialPatientItemTemplatesCreate
            ),
            grid("Each row is one template; select it to change it and its layout.", [
                column("Name", "The template's item name."),
                column("Description", "What the template's items collect."),
                column("Initial Version", "The version name new items start at.")
            ])
        ]
    },
    "/trials/:trialId/configuration/participant-item-templates/import": importGuide(
        "Copy participant item templates into this trial from the template library or another trial.",
        Permission.TrialPatientItemTemplateImport,
        [
            importColumns.name,
            importColumns.description,
            importColumns.scope,
            importColumns.selectors,
            importColumns.initialVersion
        ]
    ),
    "/trials/:trialId/configuration/participant-item-templates/:patientItemTemplateId":
        participantTemplate,
    "/trials/:trialId/configuration/participant-item-templates/create": createGuide(
        participantTemplate,
        "Add a participant item template to this trial. Importing a similar template from the library is often quicker.",
        [
            "Once saved, the item is created for every participant the template applies to. Build its layout, then save again."
        ],
        { omit: ["active"] }
    ),

    // Items
    "/trials/:trialId/items": {
        intro: "Items are the documents and data collected for the trial. Choose the kind of item to work with.",
        stops: [
            cards(
                item(
                    "All Items",
                    "Every current item across the trial, its sites, groups and participants, in one searchable list."
                ),
                item(
                    "Trial Items",
                    "Items that belong to the trial itself, such as protocol documents and approvals."
                ),
                item(
                    "Institution Items",
                    "Items each site completes, such as agreements, across all sites."
                ),
                item("Group Items", "Items collected from each group taking part in the trial."),
                item(
                    "Participant Items",
                    "Items collected for each participant, such as consent records."
                )
            )
        ]
    },
    "/trials/:trialId/items/all-items": {
        intro: "Find items across the trial, its institutions, groups and participants in one place. Each owner shows its current items together. Opening it needs the view permission of every item type.",
        stops: [
            filterStop(
                "Search owners, categories, items or versions. Inactive items are hidden unless you switch them on."
            ),
            grid("Expand an owner and select an item to open its data entry.", [
                column("Category", "The item category from the item's template."),
                column("Name", "The item's name, from its template."),
                column("Description", "What the item collects, from its template."),
                column("Version", "The item's current version."),
                column(
                    "Status",
                    "The current version's review state, separate from whether data has been submitted."
                )
            ])
        ]
    },
    "/trials/:trialId/items/trial-items": {
        intro: "The trial's own items, such as protocol documents and approvals, with their status, assignee and versions.",
        stops: [
            filterStop("Type a name, description, assignee or status to filter the list."),
            actionsStop(
                "Import creates items from trial item templates in one go; Add creates a single item.",
                Permission.TrialItemCreate
            ),
            grid("Each row is one item; select it to open its details and versions.", [
                column("Name", "The item's name, from its template."),
                column("Description", "The item's own description, if it has one."),
                column("Assigned To", "The person or role responsible for the item."),
                column("Status", "Where the item's current version is."),
                column("Versions", "How many versions the item has.")
            ])
        ]
    },
    "/trials/:trialId/items/trial-items/import": {
        intro: "Create trial items from the trial item templates. Tick the templates, then submit to create one item for each.",
        stops: [
            stop(
                importTourTargets.source,
                "Choose a source",
                "Select the template source to list. Changing the source clears the ticked templates.",
                Permission.TrialItemCreate
            ),
            grid("Tick the templates to create items from, grouped by category.", [
                column("Name", "The template's item name."),
                column("Description", "What the template's items collect."),
                column("Initial Version", "The version name the new items start at.")
            ]),
            stop(
                importTourTargets.actions,
                "Create the items",
                "Submit creates one item per ticked template; Submit And Return also goes back to the trial items.",
                Permission.TrialItemCreate
            )
        ]
    },
    "/trials/:trialId/items/trial-items/:trialItemId": trialItem,
    "/trials/:trialId/items/trial-items/create": createGuide(
        trialItem,
        "Add one trial item from a trial item template, and assign it to a person or role.",
        ["Once saved, enter its data from Enter Data."],
        { omit: ["active"] }
    ),
    "/trials/:trialId/items/trial-items/:trialItemId/entry": entryGuide(
        "this trial item",
        Permission.TrialItemUpdate
    ),
    "/trials/:trialId/items/institution-items": {
        intro: "The items trial institutions complete, such as agreements and approvals, across all sites.",
        stops: [
            filterStop("Type an institution, code or item to filter the list."),
            actionsStop(
                "Bulk Update enters the same item for many sites on one page.",
                Permission.TrialInstitutionItemInstanceUpdate
            ),
            grid("Each row is one site's item; select it to open its details and versions.", [
                column("Institution", "The site the item belongs to."),
                column("Code", "The site's institution code."),
                column("Name", "The item's name, from its template."),
                column("Description", "What the item collects, from its template."),
                column(
                    "Status",
                    "Whether the current version awaits entry, review or is Complete."
                ),
                column("Versions", "How many versions the item has.")
            ])
        ]
    },
    "/trials/:trialId/items/institution-items/update": bulkGuide(
        "sites",
        "institution",
        Permission.TrialInstitutionItemInstanceUpdate
    ),
    "/trials/:trialId/items/group-items": {
        intro: "The items collected from every group taking part in the trial.",
        stops: [
            filterStop("Type a group or item to filter the list."),
            actionsStop(
                "Bulk Update enters the same item for many groups on one page.",
                Permission.TrialGroupUpdate
            ),
            grid("Each row is one group's item; select it to open its details and versions.", [
                column("Group", "The group the item is collected from."),
                column("Name", "The item's name, from its template."),
                column("Description", "What the item collects, from its template."),
                column(
                    "Status",
                    "Whether the current version awaits entry, review or is Complete."
                ),
                column("Versions", "How many versions the item has.")
            ])
        ]
    },
    "/trials/:trialId/items/group-items/update": bulkGuide(
        "groups",
        "group",
        Permission.TrialGroupUpdate
    ),
    "/trials/:trialId/items/participant-items/update": bulkGuide(
        "participants",
        "participant",
        Permission.PatientItemUpdate
    )
};

export default trialConfigurationGuides;
